import { styled } from '@mui/material/styles';
import { Box, Typography, TextField, Chip } from '@mui/material';

// ============================================
// Modal Frame
// ============================================
export const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '900px',
  maxWidth: '95vw',
  maxHeight: '90vh',
  backgroundColor: '#fff',
  borderRadius: '16px',
  boxShadow: '0px 11px 15px -7px rgba(0,0,0,0.2)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

export const ModalHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 24px',
  height: '56px',
  flexShrink: 0,
  backgroundColor: '#262626',
});

export const ModalHeaderTitle = styled(Typography)({
  fontSize: '17px',
  fontWeight: 600,
  color: '#fff',
});

export const CloseIconButton = styled('button')({
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: '#fff',
  display: 'flex',
  opacity: 0.85,
  '&:hover': {
    opacity: 1,
  },
});

export const ModalContentArea = styled(Box)({
  padding: '24px',
  overflowY: 'auto',
  flex: 1,
});

export const ContentRow = styled(Box)({
  display: 'flex',
  gap: '24px',
});

// ============================================
// Left Section (발송 설정)
// ============================================
export const LeftSection = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  minWidth: 0,
});

export const FormRow = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const FormLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#333',
});

export const RecipientCountBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '6px',
  padding: '10px 14px',
  borderRadius: '8px',
  backgroundColor: 'rgba(74, 179, 188, 0.08)',
  border: '1px solid rgba(74, 179, 188, 0.3)',
});

export const RecipientCountText = styled(Typography)({
  fontSize: '15px',
  fontWeight: 600,
  color: '#4AB3BC',
});

export const RecipientEditButton = styled('button')({
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 600,
  color: '#4AB3BC',
  textDecoration: 'underline',
  padding: 0,
});

export const PresetRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
});

export const PresetChip = styled(Chip)({
  cursor: 'pointer',
  backgroundColor: '#F5F5F5',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: '#E8E8E8',
  },
});

export const TitleInput = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
  },
  '& .MuiFormHelperText-root': {
    textAlign: 'right',
    marginRight: 0,
  },
});

export const ContentTextArea = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
  },
  '& .MuiFormHelperText-root': {
    textAlign: 'right',
    marginRight: 0,
  },
});

// ============================================
// Right Section (회원앱 알림 미리보기)
// ============================================
export const RightSection = styled(Box)({
  width: '320px',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
});

// iOS 잠금화면/알림센터 배너를 흉내낸 미리보기.
// -apple-system 폰트 스택 + iOS 알림의 실제 여백·타이포 비율(아이콘 20px, 제목/본문 2줄
// 말줄임 등)을 그대로 따른다.
const IOS_FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Arial, sans-serif';

export const PreviewWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  backgroundColor: '#EDEDED',
  borderRadius: '20px',
  padding: '20px',
});

export const PreviewLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#666',
});

export const NotificationCard = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'rgba(246, 246, 248, 0.9)',
  backdropFilter: 'blur(20px)',
  borderRadius: '20px',
  padding: '12px 14px',
  boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 8px 20px rgba(0,0,0,0.14)',
  fontFamily: IOS_FONT_STACK,
});

// 로고 + 제목/본문을 담는 가로 행. 펼치기 화살표는 이 행 바깥(카드 전체 폭 기준)에
// 둬야 로고까지 포함한 카드 전체 가운데에 온다.
export const NotificationHeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
});

export const AppLogoImg = styled('img')({
  width: '30px',
  height: '30px',
  borderRadius: '7px',
  flexShrink: 0,
  marginTop: '1px',
});

export const NotificationTextColumn = styled(Box)({
  flex: 1,
  minWidth: 0,
});

export const NotificationTitle = styled(Typography)({
  fontSize: '15px',
  fontWeight: 600,
  color: '#000',
  wordBreak: 'break-all',
  fontFamily: IOS_FONT_STACK,
});

// 실제 알림처럼 기본은 2줄 말줄임 — expanded 일 때만 전체를 보여준다
export const NotificationBody = styled(Typography, {
  shouldForwardProp: prop => prop !== 'expanded',
})(({ expanded }) => ({
  fontSize: '15px',
  fontWeight: 400,
  color: '#000',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  marginTop: '1px',
  fontFamily: IOS_FONT_STACK,
  ...(expanded
    ? {}
    : {
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }),
}));

// 본문이 잘렸을 때만 노출되는 펼치기/접기 화살표
export const PreviewExpandToggle = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#8E8E93',
  marginTop: '2px',
  '&:hover': { color: '#4AB3BC' },
});

export const NotificationPlaceholder = styled(Typography)({
  fontSize: '14px',
  color: '#bbb',
  fontFamily: IOS_FONT_STACK,
});

// ============================================
// Footer
// ============================================
export const ButtonRow = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  padding: '16px 24px',
  borderTop: '1px solid #EEE',
});
